/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * AccessTokenService.ts
 */

import APIService from "../tools/service/APIService";
import QueryFilter from "../tools/model/pagination/Filters";
import {Entity} from "../tools/model/pagination/Entity";
import Client from "../models/Client";
import User from "../models/User";
import {AccessToken} from "../tools/model/auth/AccessToken";
import {AccessTokenFilters} from "../provider/AccessTokenDataProvider";

export interface AccessTokenWrapper extends AccessToken, Entity {
}

export enum TokenSubjectType {
    USER,
    CLIENT
}

export default class AccessTokenService extends APIService<AccessTokenWrapper, {}> {
    public static CLIENT_TOKEN_LIST = '/oauth/client/{id}/tokens';
    public static USER_TOKEN_LIST = '/oauth/user/{id}/tokens';
    public static id = "AccessTokenService";

    /**
     * revokeByClient
     * @returns {Promise<void>}
     */
    public async revokeByClient(client: Client): Promise<Boolean> {
        const route = this.httpService.buildRoute(AccessTokenService.CLIENT_TOKEN_LIST, {
            id: client.id
        });

        return await this.delete(route);
    }

    /**
     * revokeByUser
     * @returns {Promise<void>}
     */
    public async revokeByUser(user: User): Promise<Boolean> {
        const route = this.httpService.buildRoute(AccessTokenService.USER_TOKEN_LIST, {
            id: user.id
        });

        return await this.delete(route);
    }

    /**
     * findAllByUser
     * @returns {Promise<AccessToken[]>}
     */
    public async findAllByUser(user: User): Promise<AccessToken[]> {
        const route = this.httpService.buildRoute(AccessTokenService.USER_TOKEN_LIST, {
            id: user.id
        });

        const accessTokenFilters = new QueryFilter<AccessTokenFilters>({
            page: 0,
            size: 100000
        });

        return await this.getList<AccessToken, AccessTokenFilters>(route, accessTokenFilters);
    }

    /**
     * findAllByClient
     * @returns {Promise<AccessToken[]>}
     */
    public async findAllByClient(client: Client): Promise<AccessToken[]> {
        const route = this.httpService.buildRoute(AccessTokenService.CLIENT_TOKEN_LIST, {
            id: client.id
        });

        const accessTokenFilters: QueryFilter<AccessTokenFilters> = new QueryFilter<AccessTokenFilters>({
            page: 0,
            size: 100000
        });

        return await this.getList<AccessToken, AccessTokenFilters>(route, accessTokenFilters);
    }
}
